// Global Instructions Rule Applied!
import React from 'react'
import { Card, CardProps } from 'antd'

export interface FormSectionProps extends Omit<CardProps, 'title' | 'size' | 'loading' | 'className' | 'style'> {
  title?: string
  titleIcon?: boolean
  titleColor?: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'gray' | 'indigo' | 'pink' | 'yellow'
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
  size?: 'small' | 'default'
  loading?: boolean
  scrollable?: boolean
  overflowVisible?: boolean
  headerActions?: React.ReactNode
  /** Rendered at the very start of the header, before the title dot — for a
   *  leading control like the state strip's collapse chevron. */
  headerLeading?: React.ReactNode
  /** Rendered immediately after the title text, inside the same leading group.
   *  For a chip that QUALIFIES the heading rather than acting on the section —
   *  the LP strip's status Tag reads 'Current State · Target LP' there, where
   *  in headerActions it floated far right beside 'Agent-maintained' with
   *  nothing saying what it was a status of (owner, 2026-09-06). */
  titleSuffix?: React.ReactNode
  /** No body padding and no card side inset: the content draws edge to edge.
   *  For a table whose header row IS the section bar (the Deal Pipeline →
   *  Budget statement, owner 2026-11-04) - pass no `title`, and render
   *  FormSectionTitle in the first header cell so the bar reads the same. */
  flush?: boolean
}

const TITLE_DOT_CLASSES: Record<string, string> = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  gray: 'bg-gray-500',
  indigo: 'bg-indigo-500',
  pink: 'bg-pink-500',
  yellow: 'bg-yellow-500'
}

export type FormSectionTitleProps = Pick<
  FormSectionProps,
  'title' | 'titleIcon' | 'titleColor' | 'titleSuffix' | 'headerLeading'
>

/** The section bar's leading group - dot, title, suffix - on its own, for a
 *  surface that draws the bar itself (a flush FormSection's table header). */
export function FormSectionTitle({
  title,
  titleIcon = true,
  titleColor = 'blue',
  titleSuffix,
  headerLeading
}: FormSectionTitleProps): React.ReactElement {
  return (
    <div data-debug='form-section-title-section' className='flex items-center gap-2 flex-shrink-0 min-w-0'>
      {headerLeading}
      {titleIcon && (
        <div
          data-debug='form-section-icon'
          className={`w-2 h-2 rounded-full flex-shrink-0 ${TITLE_DOT_CLASSES[titleColor] || TITLE_DOT_CLASSES.blue}`}
        ></div>
      )}
      <span className='text-xs font-semibold text-gray-700 m-0 truncate'>{title}</span>
      {titleSuffix}
    </div>
  )
}

/**
 * FormSection Component
 * Reusable card component with consistent styling for forms across the application
 * Provides standardized header styling, scrolling behavior, and layout
 *
 * overflowVisible: leaves the card body in natural flow with no overflow clipping —
 * required when children use position:sticky against an ancestor scroll container
 * (e.g. a Table with sticky.getContainer pinning its summary to the tab scroller).
 */
export function FormSection({
  type,
  title,
  titleIcon = true,
  titleColor = 'blue',
  children,
  className = '',
  style = {},
  size = 'small',
  loading = false,
  scrollable = true,
  overflowVisible = false,
  headerActions,
  headerLeading,
  titleSuffix,
  flush = false,
  ...cardProps
}: FormSectionProps): React.ReactElement {
  return (
    <div
      data-debug={`form-section-wrapper-${title?.toLowerCase().replace(/[^a-z0-9]/gi, '-') || 'untitled'}`}
      className={`shadow-sm border border-gray-200 rounded-lg bg-white mb-1 ${className}`.trim()}
      style={style}
    >
      {/* Fixed Card Header */}
      {title && (
        <div
          data-debug={`form-section-header-${title?.toLowerCase().replace(/[^a-z0-9]/gi, '-')}`}
          className='bg-gray-100 px-4 h-8 rounded-t-lg flex items-center justify-between sticky top-0 z-10 min-w-0'
        >
          <FormSectionTitle
            title={title}
            titleIcon={titleIcon}
            titleColor={titleColor}
            titleSuffix={titleSuffix}
            headerLeading={headerLeading}
          />
          {headerActions && (
            <div
              data-debug='form-section-header-actions'
              className='flex items-center gap-2 flex-shrink-0 min-w-0 ml-2'
            >
              {headerActions}
            </div>
          )}
        </div>
      )}

      {/* Scrollable Card Content */}
      <Card
        data-debug={`form-section-${title?.toLowerCase().replace(/[^a-z0-9]/gi, '-') || 'untitled'}`}
        data-component='FormSection'
        data-title={title}
        data-scrollable={scrollable}
        data-size={size}
        size={size}
        loading={loading}
        className={`border-0 rounded-none ${title ? 'rounded-b-lg' : 'rounded-lg'} ${overflowVisible ? '' : scrollable ? 'overflow-y-auto overflow-x-hidden h-full' : 'flex-1 min-h-0 flex flex-col overflow-hidden'}`}
        {...cardProps}
        style={{
          paddingLeft: flush ? 0 : 5,
          paddingRight: flush ? 0 : 5
        }}
        styles={{
          body:
            !overflowVisible && scrollable
              ? {
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: flush ? 0 : '16px'
                }
              : {
                  padding: flush ? 0 : '16px'
                }
        }}
        classNames={{
          body: overflowVisible ? '' : scrollable ? 'h-full' : 'flex-1 min-h-0 flex flex-col overflow-hidden'
        }}
      >
        {/* Card Content */}
        <div
          data-debug='form-section-content'
          className={overflowVisible ? '' : scrollable ? 'h-full' : 'flex-1 min-h-0 flex flex-col overflow-hidden'}
        >
          {children}
        </div>
      </Card>
    </div>
  )
}
